import { type FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, CardGridArea, CardRowSimple, formatDateTimeFromISO, LoadingSpinner } from "widgets";

import { renderSampleAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import type { ItemChange } from "@/graphql.js";
import { RelativeUserLabel } from "@/modules/users/components/RelativeUserLabel.tsx";
import type { ISampleState } from "@/redux/modules/sample.js";
import type { IAppState } from "@/redux/reducer.js";
import { ENTITY_TYPE } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { AttributesListWidget } from "@/ui-modules/core/navigation-panel/AttributeWidget.tsx";
import { SampleLockedIcon } from "@/ui-modules/core/tiles/components/SampleTile.tsx";

import GenericEntitySummaryView from "../GenericEntitySummaryView.tsx";

export const SampleHistoryView: FC = () => {
  const { sample, isLoading } = useSelector<IAppState, ISampleState>((state) => state.sample);
  const showSpinner: boolean = isLoading || !sample;

  const records = useMemo(
    () =>
      sample?.history.map((itemChange: ItemChange) => {
        const eventUserLabel = itemChange.event?.user ? <RelativeUserLabel user={itemChange.event.user} /> : "system";

        return (
          <Card
            title={formatDateTimeFromISO(itemChange.event.created)}
            subtitle={eventUserLabel}
            area={CardGridArea.Header}
            key={`itemChange-${itemChange.id}`}
          >
            <CardRowSimple label={`${capitalizeFirstLetter(itemChange.dataComponent.name)} changes`}>
              {itemChange.event.message}
            </CardRowSimple>
            {itemChange.changes && itemChange.changes.length > 0 && (
              <CardRowSimple>
                <div>
                  <table
                    style={{
                      fontSize: 12,
                      lineHeight: 1,
                      borderCollapse: "separate",
                      borderSpacing: "20px 3px",
                      margin: "-3px -20px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Group Name</th>
                        <th>Attribute</th>
                        <th>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemChange.changes.map(
                        (change) =>
                          change && (
                            <tr key={`change-${change.id}`}>
                              <td>{change.groupName}</td>
                              <td>{change.attribute}</td>
                              <td>{change.newValue ?? "-"}</td>
                            </tr>
                          ),
                      )}
                    </tbody>
                  </table>
                </div>
              </CardRowSimple>
            )}
          </Card>
        );
      }),
    [sample?.history],
  );

  return (
    <GenericEntitySummaryView
      key="GenericEntitySummaryView"
      title={sample?.name ?? ""}
      subtitle={sample?.description || "Sample History"}
      titleTrailer={sample?.locked ? <SampleLockedIcon size={20} /> : null}
      entity={{ id: sample?.id, groupType: ENTITY_TYPE.SAMPLE }}
      headerAttributes={[
        <AttributesListWidget attributes={renderSampleAttributesWidgets(sample)} key="sample-history-view-widget-1" />,
      ]}
      project={sample?.project}
      isLoading={showSpinner}
    >
      {showSpinner ? (
        <LoadingSpinner inCenter />
      ) : records.length === 0 ? (
        <ContentPlaceholder text="No history records exist for this sample." />
      ) : (
        records
      )}
    </GenericEntitySummaryView>
  );
};
